import React from 'react';
import { composeAnswer } from './semaforo';
import { LmxText } from './LmxText';
import VoicePreviewButton from '../components/nodes/VoicePreviewButton';
import { canPreview } from '../utils/elevenVoices';

/**
 * Tarjetas del globo, portadas del prototipo chat-2-sml.html:
 * say · tag · chips · summary · cost · progress · flow · prompt · fixes ·
 * results · timeline. Un tipo = un componente chico.
 */

const Chips = ({ items, onAction }) => (  <div className="lmx-chips">
    {(items || []).map((chip, i) => {
      const label = typeof chip === 'string' ? chip : chip.label;
      const action = typeof chip === 'string'
        ? { type: 'message', text: chip }
        : (chip.action || { type: 'message', text: chip.label });
      return (
        <button
          key={i}
          type="button"
          className={chip.primary ? 'is-primary' : ''}
          onClick={() => onAction?.(action)}
          data-testid={`lmx-chip-${i}`}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const Summary = ({ fields, title }) => (
  <div className="lmx-summary" data-testid="lmx-card-summary">
    <h4>{title || 'Lo que entendí'}</h4>
    <dl>
      {Object.entries(fields || {}).map(([k, v]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt><dd><LmxText text={String(v)} inline /></dd>
        </React.Fragment>
      ))}
    </dl>
  </div>
);

const Cost = ({ rows, items, total, note }) => {
  const list = rows || (items || []).map((i) => [i.label, i.nodes ?? '', i.credits ?? i.cost ?? '']);
  const sum = total ?? list.reduce((a, r) => a + (Number(r[2]) || 0), 0);
  return (
    <>
      <table className="lmx-cost" data-testid="lmx-card-cost">
        <tbody>
          <tr>
            <th>Etapa</th>
            <th style={{ textAlign: 'right' }}>Nodos</th>
            <th style={{ textAlign: 'right' }}>Créditos</th>
          </tr>
          {list.map((r, i) => (
            <tr key={i} className={i === 0 ? 'is-first' : ''}>
              <td>{r[0]}</td><td className="n">{r[1]}</td><td className="n">{r[2]}</td>
            </tr>
          ))}
          <tr className="is-total">
            <td>Total</td><td className="n" /><td className="n">{sum}</td>
          </tr>
        </tbody>
      </table>
      {note && <p className="lmx-cost-note">{note}</p>}
    </>
  );
};

const Progress = ({ items }) => (
  <div className="lmx-runs" data-testid="lmx-card-progress">
    {(items || []).map((it, i) => (
      <div key={i} className={`lmx-run ${it.state || ''}`}>
        <i />
        <span>
          {it.label}
          {it.sub && <small>{it.sub}</small>}
        </span>
        <em>{it.meta || ''}</em>
      </div>
    ))}
  </div>
);

const Flow = ({ nodes, diff, onAction }) => (
  <div className="lmx-flow" data-testid="lmx-card-flow">
    {diff && <span className="lmx-flow-diff">{diff}</span>}
    <div className="lmx-flow-row">
      {(nodes || []).map((n, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="lmx-flow-arrow">→</span>}
          <button
            type="button"
            className={`lmx-fnode ${n.state === 'new' ? 'is-new' : ''} ${n.state === 'changed' ? 'is-changed' : ''}`}
            onClick={() => n.id && onAction?.({ type: 'focus_node', node_id: n.id })}
            title={n.id ? 'Ir al nodo' : undefined}
          >
            <b>{n.label || n.t}</b>
            {(n.meta || n.m) && <small>{n.meta || n.m}</small>}
          </button>
        </React.Fragment>
      ))}
    </div>
  </div>
);

const Prompt = ({ label, text }) => (
  <div className="lmx-prompt" data-testid="lmx-card-prompt">
    <span className="lmx-prompt-label">{label || 'Prompt'}</span>
    <p><LmxText text={text} /></p>
  </div>
);

const Fixes = ({ items }) => (
  <ol className="lmx-fixes" data-testid="lmx-card-fixes">
    {(items || []).map((f, i) => (
      <li key={i}>
        <LmxText text={f.text || f.label} inline />
        {f.note && <small>{f.note}</small>}
      </li>
    ))}
  </ol>
);

const Results = ({ items }) => (
  <div className="lmx-results" data-testid="lmx-card-results">
    {(items || []).map((r, i) => (
      <div className="lmx-res" key={i}>
        <figure>{r.url && <img src={r.url} alt={r.caption || ''} />}</figure>
        {r.caption && <p>{r.caption}</p>}
      </div>
    ))}
  </div>
);

const Timeline = ({ tracks }) => (
  <div className="lmx-tl" data-testid="lmx-card-timeline">
    {(tracks || []).map((t, i) => (
      <div className="lmx-tl-row" key={i}>
        <span>{t.name}</span>
        <div className="lmx-tl-track">
          {(t.clips || []).map((c, j) => (
            <i
              key={j}
              style={{
                left: `${c.left ?? 0}%`,
                width: `${c.width ?? 10}%`,
                background: c.color || '#7fb2ff',
              }}
            >
              {c.label}
            </i>
          ))}
        </div>
      </div>
    ))}
  </div>
);

/* Envío LMX-3 · tarjeta de marca: franja de colores, logo, @identificador,
   paleta con hex, muestra tipográfica real, tono/público/estilo y acciones. */
const loadedFonts = new Set();
const fontFamily = (name) => (name || '').replace(/\s+\d{3}$/, '').trim();
const ensureFont = (name) => {
  const fam = fontFamily(name);
  if (!fam || loadedFonts.has(fam) || typeof document === 'undefined') return;
  loadedFonts.add(fam);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fam)}:wght@400;700;800&display=swap`;
  document.head.appendChild(link);
};

const Brand = ({
  name, handle, logo_url: logoUrl, colors, fonts, tagline, tone, audience,
  visual_style: visualStyle, voice, slogans, references, voices, actions, onAction,
}) => {
  const list = colors || [];
  const titles = fonts?.titles;
  const body = fonts?.body;
  React.useEffect(() => { ensureFont(titles); ensureFont(body); }, [titles, body]);
  const weight = (titles || '').match(/(\d{3})$/)?.[1] || 800;
  const main = list[0]?.hex || '#7f8a99';
  return (
    <div className="lmx-brand" data-testid="lmx-card-brand">
      <div className="lmx-brand-strip">
        {list.map((c, i) => <i key={i} style={{ background: c.hex, flex: i === 0 ? 3 : 1 }} />)}
      </div>
      <div className="lmx-brand-body">
        <div className="lmx-brand-head">
          <div className="lmx-brand-logo" style={logoUrl ? undefined : { background: main }}>
            {logoUrl ? <img src={logoUrl} alt={`Logo de ${name}`} /> : <span>{(name || '?').slice(0, 1)}</span>}
          </div>
          <div>
            <b>{name}</b>
            {handle && <code>@{handle}</code>}
          </div>
        </div>
        {!!list.length && (
          <div className="lmx-brand-palette">
            {list.map((c, i) => (
              <div key={i} title={c.name || c.hex}>
                <i style={{ background: c.hex }} />
                <small>{c.hex}</small>
              </div>
            ))}
          </div>
        )}
        {(tagline || titles) && (
          <div className="lmx-brand-type">
            <p style={{ fontFamily: `'${fontFamily(titles)}', sans-serif`, fontWeight: weight }}>
              {(tagline || name || '').toUpperCase()}
            </p>
            {(titles || body) && (
              <small>{[titles && `${titles} títulos`, body && `${body} textos`].filter(Boolean).join(' · ')}</small>
            )}
          </div>
        )}
        <dl className="lmx-brand-facts">
          {tone && (<><dt>Tono</dt><dd>{tone}</dd></>)}
          {audience && (<><dt>Público</dt><dd>{audience}</dd></>)}
          {visualStyle && (<><dt>Estilo</dt><dd>{visualStyle}</dd></>)}
          {!voices?.length && voice && (<><dt>Voz</dt><dd>{voice}</dd></>)}
          {(voices || []).map((v) => (
            <React.Fragment key={v.role}><dt>Voz</dt><dd className="lmx-char-voice">{v.label}
              {canPreview(v) && <VoicePreviewButton voice={v.voice} testId={`lmx-brand-voice-preview-${v.role}`} />}
            </dd></React.Fragment>
          ))}
          {!!slogans?.length && (<><dt>Eslóganes</dt><dd>{slogans.map((t) => `«${t}»`).join(' · ')}</dd></>)}
        </dl>
        {/* Envío LMX-4 (I-27): imágenes de referencia. «Estilo» = sólo guía
            visual (no se usa tal cual); «Usar» = se puede usar como está. */}
        {!!references?.length && (
          <div className="lmx-brand-refs" data-testid="lmx-brand-refs">
            {references.map((r) => (
              <figure key={r.asset_id} title={r.missing ? 'La imagen ya no está en la Galería' : (r.note || '')}
                      className={r.missing ? 'is-missing' : ''}>
                {r.missing
                  ? <span className="lmx-brand-ref-missing">Falta la imagen</span>
                  : <img src={r.url} alt={r.note || 'Referencia de marca'} loading="lazy" />}
                <figcaption>{r.role === 'use' ? 'Usar' : 'Estilo'}</figcaption>
              </figure>
            ))}
          </div>
        )}
        {!!actions?.length && <Chips items={actions} onAction={onAction} />}
      </div>
    </div>
  );
};

/* Envío PJ-1 · tarjeta de PERSONAJE: foto, quién es, cómo habla, su voz (con
   muestra gratis) y sus imágenes de referencia con el rol de cada una. */
const Character = ({
  name, handle, cover_url: coverUrl, kind, role, age, summary, traits, speech,
  catchphrases, looks, visual_style: visualStyle, voice, references, missing_sheet: missingSheet,
  needs_consent: needsConsent, brand, actions, onAction,
}) => (
  <div className="lmx-brand lmx-char" data-testid="lmx-card-character">
    <div className="lmx-brand-body">
      <div className="lmx-brand-head">
        <div className="lmx-char-avatar">
          {coverUrl ? <img src={coverUrl} alt={name} /> : <span>{(name || '?').slice(0, 1)}</span>}
        </div>
        <div>
          <b>{name}</b>
          {handle && <code>@{handle}</code>}
          {(kind || role || age || brand) && (
            <small className="lmx-char-meta">{[kind, role, age, brand && `@${brand}`].filter(Boolean).join(' · ')}</small>
          )}
        </div>
      </div>
      {summary && <p className="lmx-char-summary">{summary}</p>}
      <dl className="lmx-brand-facts">
        {!!traits?.length && (<><dt>Personalidad</dt><dd>{traits.join(', ')}</dd></>)}
        {speech && (<><dt>Habla</dt><dd>{speech}</dd></>)}
        {!!catchphrases?.length && (<><dt>Frases</dt><dd>{catchphrases.map((t) => `«${t}»`).join(' · ')}</dd></>)}
        {!!looks?.length && (<><dt>Looks</dt><dd>{looks.join(' · ')}</dd></>)}
        {visualStyle && (<><dt>Estilo</dt><dd>{visualStyle}</dd></>)}
        {voice && (
          <><dt>Voz</dt><dd className="lmx-char-voice">{voice.label}
            {canPreview(voice) && <VoicePreviewButton voice={voice.voice} testId="lmx-char-voice-preview" />}
          </dd></>
        )}
      </dl>
      {!!references?.length && (
        <div className="lmx-brand-refs" data-testid="lmx-char-refs">
          {references.map((r) => (
            <figure key={r.asset_id} title={r.missing ? 'La imagen ya no está en la Galería' : r.label}
                    className={r.missing ? 'is-missing' : ''}>
              {r.missing
                ? <span className="lmx-brand-ref-missing">Falta la imagen</span>
                : <img src={r.url} alt={r.label} loading="lazy" />}
              <figcaption>{r.label}</figcaption>
            </figure>
          ))}
        </div>
      )}
      {!!missingSheet?.length && <p className="lmx-char-note">Hoja de modelo: falta {missingSheet.join(', ')}.</p>}
      {needsConsent && <p className="lmx-char-note is-warn">Persona real: falta la autorización de imagen y voz.</p>}
      {!!actions?.length && <Chips items={actions} onAction={onAction} />}
    </div>
  </div>
);

/* Envío LMX-6 · pregunta VIOLETA: opciones en botones (una o varias), la
   recomendada marcada, «Otra» con texto libre y un solo «Responder». La
   respuesta viaja como mensaje del usuario y el turno sigue desde ahí. */
const Question = ({ title, questions, onAction }) => {
  const [picked, setPicked] = React.useState({});
  const [other, setOther] = React.useState({});
  const [sent, setSent] = React.useState(false);
  const toggle = (q, opt) => setPicked((p) => {
    const cur = new Set(p[q.id] || []);
    if (q.multiple) { if (cur.has(opt)) cur.delete(opt); else cur.add(opt); }
    else { cur.clear(); cur.add(opt); }
    return { ...p, [q.id]: [...cur] };
  });
  const answer = composeAnswer(questions, picked, other);
  const send = () => {
    if (!answer || sent) return;
    setSent(true);
    onAction?.({ type: 'message', text: answer });
  };
  return (
    <div className={`lmx-q ${sent ? 'is-sent' : ''}`} data-testid="lmx-card-question" role="group" aria-label={title}>
      <div className="lmx-q-head"><span className="lmx-q-dot" aria-hidden>?</span><b>{title}</b></div>
      {(questions || []).map((q) => (
        <div className="lmx-q-item" key={q.id}>
          <p>{q.text}{q.multiple && <small> · podés elegir varias</small>}</p>
          <div className="lmx-q-opts">
            {q.options.map((opt) => {
              const on = (picked[q.id] || []).includes(opt);
              return (
                <button key={opt} type="button" disabled={sent}
                        className={`${on ? 'is-on' : ''} ${q.recommended === opt ? 'is-rec' : ''}`}
                        aria-pressed={on} onClick={() => toggle(q, opt)}>
                  {opt}{q.recommended === opt && <span className="lmx-q-rec">recomendada</span>}
                </button>
              );
            })}
          </div>
          <input type="text" disabled={sent} placeholder="Otra (escribila)"
                 value={other[q.id] || ''} onChange={(e) => setOther((o) => ({ ...o, [q.id]: e.target.value }))}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }} />
        </div>
      ))}
      <button type="button" className="lmx-q-send" disabled={!answer || sent} onClick={send}
              data-testid="lmx-question-send">
        {sent ? 'Respuesta enviada' : 'Responder'}
      </button>
    </div>
  );
};

/* Envío LMX-6b · pausa ÁMBAR con «Seguir». Si `auto`, sigue sola tras una
   cuenta regresiva corta (cancelable): el usuario no tiene que tipear «seguí». */
const Continue = ({ text, action, auto, job, onAction }) => {
  const [left, setLeft] = React.useState(auto ? 5 : null);
  const [used, setUsed] = React.useState(false);
  const go = React.useCallback(() => {
    if (used) return;
    setUsed(true);
    setLeft(null);
    onAction?.(action || { type: 'message', text: 'Seguí' });
  }, [action, onAction, used]);
  React.useEffect(() => {
    if (left == null || used) return undefined;
    if (left <= 0) { go(); return undefined; }
    const t = setTimeout(() => setLeft((n) => (n == null ? n : n - 1)), 1000);
    return () => clearTimeout(t);
  }, [left, used, go]);
  return (
    <div className={`lmx-continue ${used ? 'is-used' : ''}`} data-testid="lmx-card-continue">
      {job && (
        <span className="lmx-job-chip" style={{ '--job': job.color }}>
          <b>{job.number}</b>{job.title}
        </span>
      )}
      <p>{text}</p>
      <div className="lmx-continue-row">
        <button type="button" className="lmx-continue-go" disabled={used} onClick={go}
                data-testid="lmx-continue-go">
          {used ? 'Siguiendo…' : left != null ? `Seguir (sigo solo en ${left})` : 'Seguir'}
        </button>
        {left != null && !used && (
          <button type="button" className="lmx-continue-stop" onClick={() => setLeft(null)}>Esperar</button>
        )}
      </div>
    </div>
  );
};

/* Envío LMX-T · ya hay 2 trabajos a la vez: opciones (anotarlo en la cola o
   pausar uno de los dos). Nunca se ofrece irse a otro proyecto. */
const JobsFull = ({ text, jobs, items, onAction }) => {
  const [used, setUsed] = React.useState(false);
  return (
    <div className={`lmx-jobsfull ${used ? 'is-used' : ''}`} data-testid="lmx-card-jobs-full">
      <p>{text}</p>
      {!!jobs?.length && (
        <div className="lmx-jobsfull-list">
          {jobs.map((j) => (
            <span key={j.id} className="lmx-job-chip" style={{ '--job': j.color }}>
              <b>{j.number}</b>{j.title}
            </span>
          ))}
        </div>
      )}
      <div className="lmx-jobsfull-btns">
        {(items || []).map((it, i) => (
          <button key={i} type="button" disabled={used}
                  className={it.primary ? 'is-primary' : ''}
                  onClick={() => { setUsed(true); onAction?.(it.action); }}>
            {it.label}
          </button>
        ))}
      </div>
    </div>
  );
};

/* Envío LMX-6 · cierre VERDE: qué quedó, qué se verificó y lo que sigue. */
const Done = ({ resumen, verificado, siguientes, onAction }) => (
  <div className="lmx-done" data-testid="lmx-card-done">
    <div className="lmx-done-head"><span aria-hidden>✓</span><b>Listo</b></div>
    {resumen && <p>{resumen}</p>}
    {!!verificado?.length && (
      <ul>{verificado.map((v, i) => <li key={i}>{v}</li>)}</ul>
    )}
    {!!siguientes?.length && (
      <Chips items={siguientes.map((n, i) => (typeof n === 'string'
        ? { label: n, primary: i === 0 }
        : { label: n.label, primary: i === 0, action: n.action || { type: 'message', text: n.text || n.label } }))}
        onAction={onAction} />
    )}
  </div>
);

const NEEDS = {
  say: 'text', tag: 'text', chips: 'items', summary: 'fields', progress: 'items',
  flow: 'nodes', prompt: 'text', fixes: 'items', results: 'items', timeline: 'tracks',
  brand: 'name', character: 'name', question: 'questions', done: 'resumen', continue: 'text',
  jobs_full: 'text',
};
const blank = (v) => v == null
  || (Array.isArray(v) ? v.length === 0
    : typeof v === 'object' ? Object.keys(v).length === 0 : !String(v).trim());
export const isEmptyCard = (card) => {
  if (!card?.type) return true;
  if (card.type === 'cost') return blank(card.rows) && blank(card.items);
  const field = NEEDS[card.type];
  return field ? blank(card[field]) : false;
};

export const LmxCard = ({ card, onAction }) => {
  if (!card?.type) return null;
  switch (card.type) {
    case 'say': return <p className="lmx-say"><LmxText text={card.text} inline /></p>;
    case 'tag': return <span className={`lmx-tag ${card.level === 'warn' ? 'is-warn' : ''} ${card.level === 'error' ? 'is-error' : ''}`}>{card.text}</span>;
    case 'question': return <Question {...card} onAction={onAction} />;
    case 'done': return <Done {...card} onAction={onAction} />;
    case 'continue': return <Continue {...card} onAction={onAction} />;
    case 'jobs_full': return <JobsFull {...card} onAction={onAction} />;
    case 'chips': return <Chips {...card} onAction={onAction} />;
    case 'summary': return <Summary {...card} />;
    case 'cost': return <Cost {...card} />;
    case 'progress': return <Progress {...card} />;
    case 'flow': return <Flow {...card} onAction={onAction} />;
    case 'prompt': return <Prompt {...card} />;
    case 'fixes': return <Fixes {...card} />;
    case 'results': return <Results {...card} />;
    case 'timeline': return <Timeline {...card} />;
    case 'brand': return <Brand {...card} onAction={onAction} />;
    case 'character': return <Character {...card} onAction={onAction} />;
    default:
      return <pre className="lmx-raw">{JSON.stringify(card, null, 1)}</pre>;
  }
};

export default LmxCard;
